<template>
  <hit-base-input
    use-custom-height
    :full-width="false"
  >
    <hit-table
      ref="hit-table"
      :items="listData"
      :item-properties="itemProperties"
      item-id-property="$id"
      :config-store="configStore"
      items-selectable
      :addable="!disabled"
      :deletable="!disabled"
      dynamic-card
      :search-field="null"
      :data-service="DataService"
      disable-hide-columns
      @delete="
        (items) =>
          container.deleteRowForDynamicInputList(items, listFormLocation)
      "
    >
      <template
        v-for="property in itemProperties"
        #[property.slotName]="{formData}"
      >
        <hit-container-dynamic-input
          :key="property.slotName"
          :container="container"
          :attribute="
            sectionId + '-' + formData.$id + '-' + property.propertyName
          "
          :type="property.type"
          :route="property.route"
          :options="property.options"
          :inline-input="true"
          :tag-filters="property.tagFilters"
          :custom-filters="generateCustomFilters(property, formData)"
          :disabled="disabled"
          validate
          store-whole-object
          :browser-select-validation="browserSelectValidation"
          in-table
          @after-container-change="
            (att, data) => handleValueUpdate(att, data, sectionId, formData)
          "
        />
      </template>
      <template
        v-if="!disabled"
        #buttonAdd
      >
        <hit-button-add @click="addNewRow" />
        <hit-button
          v-if="importableFromPlanning"
          prefix-icon="import"
          :label="t('hit-app.form.import-from-planning')"
          :show-spinner="importingHoursFromPlanning"
          @click="
            importingHoursFromPlanning = true;
            $emit('import-from-planning', listFormLocation);
          "
        />
      </template>
    </hit-table>
  </hit-base-input>
</template>
<script>
import {HitBaseInput, HitButtonAdd, HitTable, HitButton} from '@hit/components';
import HitContainerDynamicInput from './HitContainerDynamicInput.vue';
import {useConfigurationStore, useUserProfileStore} from '../../../store';
import {DataService} from '../../../api';
import {useI18n} from 'vue-i18n';

export default {
  name: 'HitContainerDynamicInputList',
  setup() {
    const {t} = useI18n();
    return {t};
  },
  components: {
    HitBaseInput,
    HitTable,
    HitContainerDynamicInput,
    HitButtonAdd,
    HitButton,
  },
  props: {
    container: {
      type: Object,
      required: true,
    },
    listData: {
      type: Array,
      default: () => [],
    },
    listDef: {
      type: Object,
      default: null,
    },
    listFormLocation: {
      type: String,
      default: null,
    },
    createItemFunction: {
      type: Function,
      required: true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * In some cases, we want to check the content of a selected record before
     * triggering the update event
     * When function returns false, update is skipped
     * (Example: staff used in injection form needs to have a cost_resource_id)
     */
    browserSelectValidation: {
      type: Function,
      required: false,
      default() {
        return true;
      },
    },
    /**
     * Needed  to prefix the list input attributes
     */
    sectionId: {
      type: String,
      required: false,
      default: null,
    },

    /**
     * If filters with absolute paths are defined we need the entire form data to replace the variable
     */
    entireFormData: {
      type: Object,
      required: false,
      default: () => {},
    },
  },
  data() {
    return {
      importingHoursFromPlanning: false,
      configStore: useConfigurationStore(),
      userStore: useUserProfileStore(),
    };
  },
  methods: {
    generateCustomFilters(property, formData = {}) {
      if (!property?.customFilters || property?.customFilters?.length === 0)
        return {};
      const processedFilters = {};
      property.customFilters.forEach((filter) => {
        if (/^.*\{.*\}.*$/.test(filter.value)) {
          const matches = filter.value.match(/\{([^}]+)\}/);
          const pathParts = matches[1].split('.');

          let value = null;
          if (pathParts[0][0] === '#') {
            pathParts[0] = pathParts[0].slice(1);
            value = this.entireFormData;
          } else {
            value = formData;
          }

          for (const part of pathParts) {
            if (value && value[part]) {
              value = value[part];
            } else {
              value = 'FILTER_ERROR';
              break;
            }
          }
          if (value !== 'FILTER_ERROR') {
            processedFilters[filter.column] = filter.value.replace(
              matches[0],
              value
            );
          } else {
            return {};
          }
        } else {
          processedFilters[filter.column] = filter.value;
        }
      });
      return processedFilters;
    },

    handleValueUpdate(attribute, data, sectionId, formData) {
      this.itemProperties.forEach((prop) => {
        if (prop.customFilters) {
          prop.customFilters.forEach((filter) => {
            if (/^.*\{.*\}.*$/.test(filter.value)) {
              const matches = filter.value.match(/\{([^}]+)\}/);
              const relatedCol = matches[1].split('.')[0];
              if (attribute.includes(relatedCol)) {
                const attToReset =
                  sectionId + '-' + formData.$id + '-' + prop.propertyName;
                this.container.handleChange(attToReset, {});
              }
            }
          });
        }
      });
    },

    getPropertyDefinitionType(propertyName) {
      if (
        this.listDef &&
        this.listDef.content &&
        this.listDef.content[propertyName]
      ) {
        return this.listDef.content[propertyName].definition.type;
      }
      return 'text';
    },

    addNewRow() {
      const newItem = this.container.createRowForDynamicInputList(
        this.itemProperties,
        this.listFormLocation
      );
      this.$refs['hit-table'].scrollTo(
        `.hit-table-line-item-${newItem['$id']}`
      );
    },
  },
  computed: {
    DataService() {
      return DataService;
    },
    importableFromPlanning() {
      const injections = this.listDef?.injection;
      if (injections) {
        for (const [injectionId, injection] of Object.entries(injections)) {
          if (injection.type == 'working-hours') {
            return true;
          }
        }
      }
      return false;
    },
    itemProperties() {
      if (!this.listDef) {
        return [];
      }
      let result = [];
      for (const propKey in this.listDef.content) {
        let prop = this.listDef.content[propKey];
        let header = prop.definition.label;
        if (header && typeof header !== 'string') {
          header = header[this.userStore.localeSnakeCase];
        }
        result.push({
          header: header,
          order: prop.definition.order,
          propertyName: propKey,
          slotName: 'item.' + propKey + '.display',
          type: this.getPropertyDefinitionType(propKey),
          route: prop.definition.reference,
          tagFilters: prop.definition?.attributeFilter?.tag ?? null,
          customFilters:
            prop.definition?.attributeFilter?.customFilters ?? null,
          options: prop.definition?.values ?? [],
        });
      }
      result.sort((a, b) => {
        return a.order - b.order;
      });
      return result;
    },
  },

  watch: {
    listData() {
      this.importingHoursFromPlanning = false;
    },
  },
};
</script>
